import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, CheckCheck, Loader2, ArrowLeft, Send, Plus, Mic } from 'lucide-react';
import { ConsumerLayout } from '@/components/consumer/ConsumerLayout';
import { GravadorAudioBar } from '@/components/painel/GravadorAudioBar';
import { useChatConsumer } from '@/hooks/useChatApp';
import { useGravadorAudio } from '@/hooks/useGravadorAudio';
import { getChatAppSignedUrl, type Mensagem } from '@/services/chat-app.service';
import { tiqueLeituraApp } from '@/lib/chat-app';
import { useTypingIndicator } from '@/hooks/useTypingIndicator';
import { useAuthContext } from '@/contexts/AuthContext';

function MediaBubble({ m }: { m: Mensagem }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (m.media_storage_path)
      getChatAppSignedUrl(m.media_storage_path)
        .then((u) => { if (alive) setUrl(u); })
        .catch(() => {});
    return () => { alive = false; };
  }, [m.media_storage_path]);
  if (!url) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (m.tipo_media === 'audio') return <audio controls src={url} className="max-w-full" />;
  return <img src={url} alt="" className="rounded-lg max-w-[12rem]" />;
}

export default function ConsumerChat() {
  const { t } = useTranslation();
  const { clienteId } = useParams();
  const { mensagens, enviar, enviarComMedia, marcarLidas } = useChatConsumer(clienteId);
  const gravador = useGravadorAudio();
  const { otherTyping, notifyTyping } = useTypingIndicator(clienteId, 'consumer');
  const { consumer } = useAuthContext();
  const inicialConsumer = consumer?.nome?.charAt(0)?.toUpperCase() || '?';
  const [texto, setTexto] = useState('');
  const [mostrarGravador, setMostrarGravador] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const fimRef = useRef<HTMLDivElement>(null);

  // Mark messages as read when clienteId changes or new messages arrive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (clienteId) marcarLidas.mutate(); }, [clienteId, mensagens.data?.length]);

  // Scroll to bottom on new messages
  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensagens.data?.length]);

  const onEnviarTexto = (e: React.FormEvent) => {
    e.preventDefault();
    const v = texto.trim();
    if (!v || !clienteId) return;
    enviar.mutate({ clienteId, conteudo: v });
    setTexto('');
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && clienteId) enviarComMedia.mutate({ file: f, tipoMedia: 'image' });
    if (fileRef.current) fileRef.current.value = '';
  };

  const onIniciarGravacao = async () => {
    setMostrarGravador(true);
    await gravador.iniciar();
  };

  const onEnviarAudio = () => {
    if (!gravador.mp3Blob || !clienteId) return;
    const file = new File([gravador.mp3Blob], 'audio.mp3', { type: 'audio/mpeg' });
    enviarComMedia.mutate({ file, tipoMedia: 'audio' });
    gravador.cancelar();
    setMostrarGravador(false);
  };

  const onCancelarGravacao = () => {
    gravador.cancelar();
    setMostrarGravador(false);
  };

  return (
    <ConsumerLayout hideNav>
      <div className="flex flex-col h-[calc(100dvh-4rem)] px-4 pb-2">
        {/* Header */}
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <Link to="/app/conversas" aria-label={t('chat.back')}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <span className="font-bold">{t('chat.title')}</span>
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto py-3 flex flex-col gap-2">
          {mensagens.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (mensagens.data ?? []).map((m) => {
            const own = m.remetente === 'cliente';
            return (
              <div key={m.id} className={`flex items-end gap-2 ${own ? 'self-end flex-row-reverse' : 'self-start'} max-w-[85%]`}>
                {own && (
                  <span className="flex items-center justify-center overflow-hidden rounded-full h-6 w-6 bg-primary shrink-0 text-[10px] text-primary-foreground font-bold">
                    {consumer?.avatar_url
                      ? <img src={consumer.avatar_url} alt="" className="h-full w-full object-cover" />
                      : inicialConsumer}
                  </span>
                )}
                <div
                  className={`rounded-lg px-3 py-2 text-sm ${
                    own ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}
                >
                  {m.tipo_media ? <MediaBubble m={m} /> : m.conteudo}
                  {own && (
                    <span className="inline-flex ml-1 align-middle">
                      {tiqueLeituraApp(m) === 'lida'
                        ? <CheckCheck className="h-3 w-3" style={{ color: 'hsl(var(--chat-read-tick))' }} aria-label="lida" />
                        : <Check className="h-3 w-3 opacity-70" aria-label="enviada" />}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={fimRef} />
        </div>

        {/* Audio recorder bar (shown when recording) */}
        {mostrarGravador && (
          <div className="border-t border-border pt-2">
            <GravadorAudioBar
              estado={gravador.estado}
              tempo={gravador.tempo}
              previewUrl={gravador.previewUrl}
              enviando={enviarComMedia.isPending}
              onParar={gravador.parar}
              onCancelar={onCancelarGravacao}
              onEnviar={onEnviarAudio}
            />
          </div>
        )}

        {/* Typing indicator */}
        {otherTyping && <div className="text-xs text-muted-foreground px-1 pb-1">{t('chat.typing')}</div>}

        {/* Input bar (hidden while recorder is active) */}
        {!mostrarGravador && (
          <form onSubmit={onEnviarTexto} className="flex items-center gap-2 pt-2 border-t border-border">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
            <button
              type="button"
              className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full text-foreground hover:bg-muted"
              onClick={() => fileRef.current?.click()}
              aria-label={t('chat.attach')}
            >
              <Plus className="h-6 w-6" />
            </button>
            <input
              className="flex-1 h-11 rounded-full bg-muted px-4 text-sm border-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={texto}
              onChange={(e) => { setTexto(e.target.value); notifyTyping(); }}
              placeholder={t('chat.placeholder')}
            />
            {texto.trim() || !gravador.suportado ? (
              <button
                type="submit"
                className="flex items-center justify-center h-11 w-11 shrink-0 rounded-full bg-primary text-primary-foreground disabled:opacity-50"
                disabled={enviar.isPending || !texto.trim()}
                aria-label={t('chat.send')}
              >
                <Send className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                className="flex items-center justify-center h-11 w-11 shrink-0 rounded-full bg-primary text-primary-foreground"
                onClick={onIniciarGravacao}
                aria-label={t('chat.record')}
              >
                <Mic className="h-5 w-5" />
              </button>
            )}
          </form>
        )}
      </div>
    </ConsumerLayout>
  );
}
